import { restoreScriptBackup } from "../backup";
import { playbackRepository, scriptRepository } from "../db";
import { CollaborativeSession } from "../collaborative-session";
import type { PlaybackSyncState } from "../collaboration-protocol";
import { replaceScriptSource } from "../document-operations";
import { exportJson, exportMarkdown, exportText, safeExportName } from "../exports";
import { exportFdx, exportFountain } from "../screenplay-format";
import { selectSmartReviewLines, updateMemorisationStat } from "../memorisation";
import { allEntries, parseScript } from "../parser";
import { playbackUpdateRequiresFullRender } from "../playback-display";
import { similarity } from "../recognition";
import { normalizeScriptDocument, parseCommaSeparated, parseWordList } from "../script-normalizer";
import { browserSpeech } from "../speech";
import { store } from "../store";
import type { ParserMode, RevealMode, ScriptDocument, ScriptEntry } from "../types";
import { qrDataUrl, readQrImage, scanQrWithCamera } from "../qr-code";
import { downloadFile, LightElement } from "../ui";
import { bindPlayerControls, nextPlaybackPosition, shouldContinueVoicePlayback, type PlayerControlActions } from "./script-player-controls";
import { renderLineText, renderPlayerView } from "./script-player-view";

export class ScriptPlayer extends LightElement {
  private script?: ScriptDocument;
  private allLines: ScriptEntry[] = [];
  private lines: ScriptEntry[] = [];
  private position = 0;
  private revealedWords = 0;
  private skipOtherLines = false;
  private voices: SpeechSynthesisVoice[] = [];
  private listening = false;
  private speaking = false;
  private speechToken = 0;
  private feedback = "";
  private smartMode = false;
  private lineShown = false;
  private renderAbort?: AbortController;
  private offerQr?: string;
  private answerQr?: string;
  private offerQrSource = "";
  private answerQrSource = "";
  private localScriptBeforeJoin?: ScriptDocument;
  private localPositionBeforeJoin = 0;
  private joinLobby = false;
  private collaboration = new CollaborativeSession({
    changed: () => { void this.refreshCollaborationCodes(); this.render(); },
    receivedScript: (script, state) => this.receiveCollaborativeScript(script, state),
    receivedState: (state) => this.applyCollaborativeState(state),
    roleUnlinked: (roleId) => this.applyRoleUnlink(roleId),
  }, () => this.collaborationPlaybackState());

  async connectedCallback(): Promise<void> {
    const scriptId = this.getAttribute("script-id");
    await store.load();
    if (!scriptId) {
      this.joinLobby = true;
      this.script = parseScript("Join collaborative rehearsal\nREADER: Paste or scan the host invitation to begin.", "left").document;
      this.allLines = allEntries(this.script).filter((entry) => entry.kind === "dialogue");
      this.lines = this.allLines;
      this.render();
      return;
    }
    const storedScript = await scriptRepository.get(scriptId);
    if (!storedScript) {
      this.renderMissingScript();
      return;
    }
    this.script = normalizeScriptDocument(storedScript, store.state.settings);
    this.allLines = allEntries(this.script).filter((entry) => entry.kind === "dialogue");
    this.lines = this.allLines;
    this.position = await this.restoredPosition(scriptId);
    this.voices = browserSpeech.voices();
    browserSpeech.onVoicesChanged(() => {
      this.voices = browserSpeech.voices();
      this.render();
    }, this.abort.signal);
    this.render();
  }

  disconnectedCallback(): void {
    this.renderAbort?.abort();
    this.collaboration.end();
    if (this.speaking) browserSpeech.stop();
    super.disconnectedCallback();
  }

  private async restoredPosition(scriptId: string): Promise<number> {
    const playback = await playbackRepository.get(scriptId);
    return Math.min(playback?.entryIndex ?? 0, Math.max(0, this.lines.length - 1));
  }

  private renderMissingScript(): void {
    this.innerHTML = '<section class="page"><h1>Script not found</h1><a href="#/">Return to library</a></section>';
  }

  private render(): void {
    if (!this.script) return;
    this.renderAbort?.abort();
    this.renderAbort = new AbortController();
    this.innerHTML = renderPlayerView({
      script: this.script,
      lines: this.lines,
      position: this.position,
      revealedWords: this.revealedWords,
      skipOtherLines: this.skipOtherLines,
      voices: this.voices,
      listening: this.listening,
      speaking: this.speaking,
      feedback: this.feedback,
      smartMode: this.smartMode,
      lineShown: this.lineShown,
      smartFeatureEnabled: store.state.settings.betaSmartMemorisation,
      recognitionAvailable: browserSpeech.canRecognize(),
      inheritedVoiceIds: Object.fromEntries(this.script.characters.map((character) => [character.id, store.state.settings.characterVoiceDefaults[character.name.trim().toLowerCase()] ?? store.state.settings.defaultVoiceId])),
      collaboration: { featureEnabled: store.state.settings.enableCollaborativeRehearsal, supported: this.collaboration.supported, ...this.collaboration.viewState(), localName: this.collaboration.viewState().localName || store.state.settings.collaborationName, offerQr: this.offerQr, answerQr: this.answerQr },
    });
    bindPlayerControls(this, this.controlActions(), this.renderAbort.signal);
    this.bindCollaborationControls(this.renderAbort.signal);
  }

  private controlActions(): PlayerControlActions {
    return {
      toggleSmartReview: () => this.toggleSmartReview(),
      scoreSmartReview: (score) => this.scoreSmartReview(score),
      selectRole: (id, selected) => this.selectRole(id, selected),
      setSkipOtherLines: (enabled) => { this.skipOtherLines = enabled; },
      setRevealMode: (mode) => this.setRevealMode(mode),
      setLineVisibility: (key, enabled) => this.setLineVisibility(key, enabled),
      adjustWordReveal: (direction) => this.adjustWordReveal(direction),
      revealLine: () => this.revealCurrentLine(),
      setVoiceReading: (key, enabled) => this.updateVoiceSetting(key, enabled),
      assignVoice: (id, voice) => this.assignVoice(id, voice),
      setPlaybackSpeed: (speed) => this.setPlaybackSpeed(speed),
      setLanguage: (language) => this.setLanguage(language),
      setRecognitionThreshold: (threshold) => this.setRecognitionThreshold(threshold),
      setIgnoredWords: (words) => this.setIgnoredWords(words),
      setTags: (tags) => this.setTags(tags),
      setNotes: (notes) => this.setNotes(notes),
      replaceSource: (sourceText, parserMode) => this.replaceSource(sourceText, parserMode),
      previousLine: () => this.move(-1),
      nextLine: () => this.move(1),
      advanceFromLine: () => this.move(1, this.speaking),
      speak: () => this.speak(),
      listen: () => this.listenForLine(),
      goTo: (position) => this.goTo(position),
      export: (format) => this.exportScript(format),
    };
  }

  private scoreSmartReview(score: number): void {
    const line = this.currentLine();
    if (!line) return;
    this.recordAttempt(line, score);
    this.move(1);
  }

  private selectRole(characterId: string, selected: boolean): void {
    if (!this.script) return;
    const selectedRoles = new Set(this.script.settings.selectedCharacterIds);
    selected ? selectedRoles.add(characterId) : selectedRoles.delete(characterId);
    this.script.settings.selectedCharacterIds = [...selectedRoles];
    void this.persist();
    this.collaboration.updateProfile(this.script.settings.selectedCharacterIds);
    this.render();
  }

  private setRevealMode(mode: RevealMode): void {
    if (!this.script) return;
    this.script.settings.revealMode = mode;
    this.revealedWords = 0;
    void this.persist();
    this.render();
  }

  private setLineVisibility(key: "hideMine" | "hideOthers" | "showOtherLinesNormally", enabled: boolean): void {
    if (!this.script) return;
    this.script.settings[key] = enabled;
    this.lineShown = false;
    void this.persist();
    this.render();
  }

  private adjustWordReveal(direction: -1 | 1): void {
    const line = this.currentLine();
    if (!line) return;
    const wordCount = line.text.trim().split(/\s+/).filter(Boolean).length;
    if (direction === 1 && this.revealedWords >= wordCount) {
      this.move(1, this.speaking);
      return;
    }
    if (direction === -1 && this.revealedWords <= 0) {
      const previousPosition = this.position;
      this.move(-1, this.speaking);
      if (this.position !== previousPosition) {
        this.revealedWords = this.currentLine()?.text.trim().split(/\s+/).filter(Boolean).length ?? 0;
        this.render();
      }
      return;
    }
    this.revealedWords = Math.max(0, Math.min(wordCount, this.revealedWords + direction));
    this.render();
    this.collaboration.broadcastState();
  }

  private revealCurrentLine(): void {
    const line = this.currentLine();
    if (line && this.smartMode) this.recordAttempt(line, 0, true);
    this.lineShown = true;
    this.render();
    this.collaboration.broadcastState();
  }

  private assignVoice(characterId: string, voiceId: string | undefined): void {
    const character = this.script?.characters.find((item) => item.id === characterId);
    if (!character) return;
    character.voiceId = voiceId;
    void this.persist();
  }

  private setPlaybackSpeed(speed: number): void {
    if (!this.script) return;
    this.script.settings.voiceReading.speed = speed;
    void this.persist();
  }

  private setLanguage(language: string): void {
    if (!this.script) return;
    this.script.settings.voiceReading.language = language;
    void this.persist();
  }

  private setRecognitionThreshold(threshold: number): void {
    if (!this.script) return;
    this.script.settings.recognition.threshold = threshold;
    void this.persist();
  }

  private setIgnoredWords(value: string): void {
    if (!this.script) return;
    this.script.settings.recognition.ignoredWords = parseWordList(value);
    void this.persist();
  }

  private setTags(value: string): void {
    if (!this.script) return;
    this.script.tags = parseCommaSeparated(value);
    void this.persist();
  }

  private setNotes(value: string): void {
    if (!this.script) return;
    this.script.notes = value.trim();
    void this.persist();
  }

  private replaceSource(sourceText: string, parserMode: ParserMode): void {
    if (!this.script || this.collaboration.viewState().mode !== "inactive") return;
    this.script = replaceScriptSource(this.script, sourceText, parserMode).document;
    this.allLines = allEntries(this.script).filter((entry) => entry.kind === "dialogue");
    this.lines = this.allLines;
    this.smartMode = false;
    this.position = Math.min(this.position, Math.max(0, this.lines.length - 1));
    this.revealedWords = 0;
    this.lineShown = false;
    this.feedback = "Original source updated and reparsed.";
    void this.persist();
    this.render();
    this.savePosition();
  }

  private updateVoiceSetting(key: "readOthersLines" | "readMyLines" | "autoProceed", value: boolean): void {
    if (!this.script) return;
    this.script.settings.voiceReading[key] = value;
    void this.persist();
  }

  private toggleSmartReview(): void {
    if (!this.script || this.collaboration.viewState().mode !== "inactive") return;
    this.lines = this.smartMode
      ? this.allLines
      : selectSmartReviewLines(this.allLines, this.script.memorisation, this.script.settings.selectedCharacterIds, this.script.settings.recognition.threshold);
    this.smartMode = !this.smartMode;
    this.resetPosition();
    this.render();
  }

  private recordAttempt(line: ScriptEntry, score: number, revealed = false): void {
    if (!this.script) return;
    this.script.memorisation[line.id] = updateMemorisationStat(this.script.memorisation[line.id], score, this.script.settings.recognition.threshold, revealed);
    void this.persist();
  }

  private goTo(position: number): void {
    if (this.collaboration.isClient() || !Number.isFinite(position) || !this.lines.length) return;
    if (this.speaking) {
      this.speechToken += 1;
      browserSpeech.stop();
      this.speaking = false;
    }
    this.position = Math.max(0, Math.min(this.lines.length - 1, position));
    this.revealedWords = 0;
    this.lineShown = false;
    this.feedback = "";
    this.render();
    this.savePosition();
    this.collaboration.broadcastState();
  }

  private move(direction: -1 | 1, voicePlaybackWasActive?: boolean): void {
    if (this.collaboration.isClient() || !this.script || !this.lines.length) return;
    const selectedRoles = new Set(this.script.settings.selectedCharacterIds);
    const nextPosition = nextPlaybackPosition(this.position, direction, this.lines.length, (position) => this.shouldSkip(position, selectedRoles));
    if (nextPosition === this.position) {
      this.render();
      return;
    }
    this.goTo(nextPosition);
    const linePlaybackEnabled = this.shouldReadCurrentLine(selectedRoles);
    const startVoicePlayback = voicePlaybackWasActive === undefined
      ? linePlaybackEnabled
      : shouldContinueVoicePlayback(voicePlaybackWasActive, linePlaybackEnabled);
    if (startVoicePlayback) this.speak();
  }

  private shouldSkip(position: number, selectedRoles: Set<string>): boolean {
    return this.skipOtherLines && position >= 0 && position < this.lines.length && !selectedRoles.has(this.lines[position]?.speakerId ?? "");
  }

  private shouldReadCurrentLine(selectedRoles: Set<string>): boolean {
    if (!this.script) return false;
    const isMine = selectedRoles.has(this.currentLine()?.speakerId ?? "");
    return isMine ? this.script.settings.voiceReading.readMyLines : this.script.settings.voiceReading.readOthersLines;
  }

  private speak(): void {
    if (this.collaboration.isClient()) return;
    if (this.speaking) {
      this.speechToken += 1;
      browserSpeech.stop();
      this.speaking = false;
      this.render();
      this.collaboration.broadcastState();
      return;
    }
    const line = this.currentLine();
    if (!line || !this.script) return;
    const settings = this.script.settings.voiceReading;
    const speechText = line.text;
    const spokenLineId = line.id;
    const token = ++this.speechToken;
    this.revealedWords = 0;
    this.speaking = true;
    this.render();
    this.collaboration.broadcastState();
    browserSpeech.speak({
      text: speechText,
      language: settings.language,
      rate: settings.speed,
      pitch: settings.pitch,
      volume: settings.volume,
      voice: this.voiceFor(line),
      onBoundary: (charIndex) => {
        if (token === this.speechToken) this.revealSpokenWord(spokenLineId, speechText, charIndex);
      },
      onEnd: () => {
        if (token !== this.speechToken) return;
        this.revealedWords = line.text.trim().split(/\s+/).filter(Boolean).length;
        this.speaking = false;
        if (settings.autoProceed) this.move(1);
        else { this.render(); this.collaboration.broadcastState(); }
      },
    });
  }

  private revealSpokenWord(lineId: string, text: string, charIndex: number): void {
    if (this.currentLine()?.id !== lineId) return;
    const spokenText = text.slice(0, charIndex);
    const completedWords = spokenText.trim() ? spokenText.trim().split(/\s+/).length : 0;
    const currentCharacterStartsWord = charIndex < text.length && /\S/u.test(text[charIndex] ?? "") && (charIndex === 0 || /\s/u.test(text[charIndex - 1] ?? ""));
    const revealedWords = completedWords + (currentCharacterStartsWord ? 1 : 0);
    if (revealedWords <= this.revealedWords) return;
    this.revealedWords = revealedWords;
    this.lineShown = true;
    this.updateCurrentLineDisplay();
    this.collaboration.broadcastState();
  }

  /** Speech boundaries are frequent; update only the line instead of replacing
   * the whole player and its focused controls on every spoken word. */
  private updateCurrentLineDisplay(): void {
    const line = this.currentLine();
    if (!line || !this.script) return;
    const selectedRoles = new Set(this.script.settings.selectedCharacterIds);
    const isMine = selectedRoles.has(line.speakerId ?? "");
    const lineElement = this.querySelector<HTMLElement>(".line-card__text");
    if (lineElement) {
      lineElement.innerHTML = renderLineText({
        script: this.script,
        smartMode: this.smartMode,
        revealedWords: this.revealedWords,
        lineShown: this.lineShown,
        speaking: this.speaking,
      }, line, isMine);
    }
    const wordCount = line.text.trim().split(/\s+/).filter(Boolean).length;
    const wordProgress = this.querySelector<HTMLElement>(".word-controls span");
    if (wordProgress) wordProgress.textContent = `${Math.min(this.revealedWords, wordCount)} / ${wordCount}`;
  }

  private voiceFor(line: ScriptEntry): SpeechSynthesisVoice | undefined {
    const character = this.script?.characters.find((item) => item.id === line.speakerId);
    const inheritedVoiceId = character ? store.state.settings.characterVoiceDefaults[character.name.trim().toLowerCase()] : undefined;
    const assignedVoice = this.voices.find((voice) => voice.voiceURI === (character?.voiceId ?? inheritedVoiceId ?? store.state.settings.defaultVoiceId));
    if (assignedVoice) return assignedVoice;
    const language = this.script?.settings.voiceReading.language.toLowerCase().split("-")[0] ?? "";
    return this.voices.find((voice) => voice.lang.toLowerCase().startsWith(language));
  }

  private listenForLine(): void {
    if (this.collaboration.isClient()) return;
    const expected = this.currentLine();
    if (!expected || !this.script) return;
    this.listening = true;
    this.feedback = "Listening…";
    this.render();
    browserSpeech.recognize(
      this.script.settings.voiceReading.language,
      (result) => this.handleRecognitionResult(expected, result.transcript),
      () => { this.feedback = "I couldn’t hear that. Try again or advance manually."; },
      () => { this.listening = false; this.render(); },
    );
  }

  private handleRecognitionResult(expected: ScriptEntry, transcript: string): void {
    if (!this.script) return;
    const recognition = this.script.settings.recognition;
    const score = similarity(expected.text, transcript, recognition.ignoredWords);
    if (this.smartMode) this.recordAttempt(expected, score);
    this.feedback = score >= recognition.threshold
      ? `Nice work — ${Math.round(score * 100)}% match.`
      : `Heard “${transcript}” — ${Math.round(score * 100)}% match; ${Math.round(recognition.threshold * 100)}% required.`;
    if (score >= recognition.threshold) window.setTimeout(() => this.move(1), 700);
  }

  private bindCollaborationControls(signal: AbortSignal): void {
    const click = (selector: string, action: () => void) => this.querySelector<HTMLElement>(selector)?.addEventListener("click", action, { signal });
    click("[data-start-host]", () => this.startHosting());
    click("[data-add-participant]", () => void this.collaboration.createInvite(store.state.settings.stunServers));
    click("[data-accept-answer]", () => {
      const value = this.querySelector<HTMLTextAreaElement>("[data-collab-answer-input]")?.value ?? "";
      if (value.trim()) void this.collaboration.acceptAnswer(value);
    });
    click("[data-join-collaboration]", () => {
      const value = this.querySelector<HTMLTextAreaElement>("[data-collab-offer-input]")?.value ?? "";
      if (value.trim()) void this.joinCollaboration(value);
    });
    click("[data-end-collaboration]", () => this.endCollaboration());
    click("[data-share-invitation]", () => void this.shareCollaborationCode("invitation"));
    click("[data-share-answer]", () => void this.shareCollaborationCode("answer"));
    click("[data-camera-offer]", () => void this.scanCollaborationCamera("offer"));
    click("[data-camera-answer]", () => void this.scanCollaborationCamera("answer"));
    this.querySelectorAll<HTMLButtonElement>("[data-unlink-participant]").forEach((button) => button.addEventListener("click", () => this.collaboration.unlinkRole(button.dataset.unlinkParticipant ?? "", button.dataset.unlinkRole ?? ""), { signal }));
    this.querySelectorAll<HTMLButtonElement>("[data-copy-collab]").forEach((button) => button.addEventListener("click", () => {
      const state = this.collaboration.viewState();
      void navigator.clipboard.writeText(button.dataset.copyCollab === "offer" ? state.offer : state.answer);
    }, { signal }));
    const scanOffer = this.querySelector<HTMLInputElement>("[data-scan-offer]");
    scanOffer?.addEventListener("change", () => { const file = scanOffer.files?.[0]; if (file) void readQrImage(file).then((value) => this.joinCollaboration(value)).catch((error) => alert(error instanceof Error ? error.message : "Could not read the QR code.")); }, { signal });
    const scanAnswer = this.querySelector<HTMLInputElement>("[data-scan-answer]");
    scanAnswer?.addEventListener("change", () => { const file = scanAnswer.files?.[0]; if (file) void readQrImage(file).then((value) => this.collaboration.acceptAnswer(value)).catch((error) => alert(error instanceof Error ? error.message : "Could not read the QR code.")); }, { signal });
  }

  private startHosting(): void {
    if (!this.script) return;
    const name = this.collaborationName();
    if (this.smartMode) { this.smartMode = false; this.lines = this.allLines; this.position = Math.min(this.position, Math.max(0, this.lines.length - 1)); }
    try {
      this.collaboration.startHost(this.script, name, this.script.settings.selectedCharacterIds);
      void this.saveCollaborationName(name);
    } catch (error) { alert(error instanceof Error ? error.message : "Could not start collaborative rehearsal."); }
  }

  private async joinCollaboration(offer: string): Promise<void> {
    if (!this.localScriptBeforeJoin && this.script) {
      this.localScriptBeforeJoin = structuredClone(this.script);
      this.localPositionBeforeJoin = this.position;
    }
    const name = this.collaborationName();
    try {
      await this.collaboration.join(offer, store.state.settings.stunServers, name);
      if (name) await this.saveCollaborationName(name);
    } catch (error) { alert(error instanceof Error ? error.message : "Could not join collaborative rehearsal."); }
  }

  private endCollaboration(): void {
    const wasClient = this.collaboration.isClient();
    this.collaboration.end();
    this.offerQr = undefined;
    this.answerQr = undefined;
    this.offerQrSource = "";
    this.answerQrSource = "";
    if (wasClient && this.localScriptBeforeJoin) {
      this.script = this.localScriptBeforeJoin;
      this.allLines = allEntries(this.script).filter((entry) => entry.kind === "dialogue");
      this.lines = this.allLines;
      this.position = Math.min(this.localPositionBeforeJoin, Math.max(0, this.lines.length - 1));
      this.localScriptBeforeJoin = undefined;
      this.revealedWords = 0;
      this.lineShown = false;
      this.speaking = false;
    }
    this.render();
  }

  private collaborationName(): string { return this.querySelector<HTMLInputElement>("[data-collab-name]")?.value.trim() || store.state.settings.collaborationName; }

  private async saveCollaborationName(name: string): Promise<void> {
    if (!name || name === store.state.settings.collaborationName) return;
    await store.saveSettings({ ...store.state.settings, collaborationName: name });
  }

  private async shareCollaborationCode(kind: "invitation" | "answer"): Promise<void> {
    const state = this.collaboration.viewState();
    const text = kind === "invitation" ? state.offer : state.answer;
    if (!text) return;
    const title = kind === "invitation" ? `Join ${state.localName}'s LineByLine rehearsal` : `LineByLine answer from ${state.localName}`;
    const joinUrl = `${location.origin}${location.pathname}#/join`;
    const sharedText = kind === "invitation" ? `${title}\n\nOpen ${joinUrl}, enter your name, and paste this invitation:\n\n${text}` : `${title}\n\nSend this answer back to the host:\n\n${text}`;
    if (navigator.share) {
      try { await navigator.share({ title, text: sharedText }); return; } catch (error) { if (error instanceof DOMException && error.name === "AbortError") return; }
    }
    await navigator.clipboard.writeText(text);
    alert(`${kind === "invitation" ? "Invitation" : "Answer"} copied. Paste it into Messages, Mail, or another app.`);
  }

  private async scanCollaborationCamera(kind: "offer" | "answer"): Promise<void> {
    try {
      const value = await scanQrWithCamera();
      if (kind === "offer") await this.joinCollaboration(value);
      else await this.collaboration.acceptAnswer(value);
    } catch (error) { if (!(error instanceof Error) || !error.message.includes("cancelled")) alert(error instanceof Error ? error.message : "Could not scan the QR code."); }
  }

  private applyRoleUnlink(roleId: string): void {
    if (!this.script) return;
    this.script.settings.selectedCharacterIds = this.script.settings.selectedCharacterIds.filter((id) => id !== roleId);
    this.render();
  }

  private async refreshCollaborationCodes(): Promise<void> {
    const state = this.collaboration.viewState();
    if (state.offer !== this.offerQrSource) {
      this.offerQrSource = state.offer;
      this.offerQr = state.offer ? await qrDataUrl(state.offer) : undefined;
      if (this.collaboration.viewState().offer === state.offer) this.render();
    }
    if (state.answer !== this.answerQrSource) {
      this.answerQrSource = state.answer;
      this.answerQr = state.answer ? await qrDataUrl(state.answer) : undefined;
      if (this.collaboration.viewState().answer === state.answer) this.render();
    }
  }

  private receiveCollaborativeScript(script: ScriptDocument, state?: PlaybackSyncState): void {
    this.script = restoreScriptBackup(JSON.stringify(script), store.state.settings);
    this.script.settings.selectedCharacterIds = [];
    this.collaboration.updateProfile([]);
    this.allLines = allEntries(this.script).filter((entry) => entry.kind === "dialogue");
    this.lines = this.allLines;
    this.smartMode = false;
    this.position = 0;
    this.revealedWords = 0;
    this.lineShown = false;
    this.speaking = false;
    if (state) this.applyCollaborativeState(state);
    else this.render();
  }

  private applyCollaborativeState(state: PlaybackSyncState): void {
    const position = this.lines.findIndex((line) => line.id === state.lineId);
    if (position < 0) return;
    const needsFullRender = playbackUpdateRequiresFullRender(
      { lineId: this.currentLine()?.id ?? "", speaking: this.speaking },
      { lineId: state.lineId, speaking: state.speaking },
    );
    if (this.speaking && !state.speaking) browserSpeech.stop();
    this.position = position;
    this.revealedWords = Math.max(0, state.revealedWords);
    this.lineShown = state.lineShown;
    this.speaking = state.speaking;
    this.feedback = "";
    if (needsFullRender) this.render();
    else this.updateCurrentLineDisplay();
  }

  private collaborationPlaybackState(): Omit<PlaybackSyncState, "sequence" | "sentAt"> {
    return {
      lineId: this.currentLine()?.id ?? "",
      fallbackIndex: this.position,
      revealedWords: this.revealedWords,
      lineShown: this.lineShown,
      speaking: this.speaking,
    };
  }

  private exportScript(format: string): void {
    if (!this.script) return;
    const name = safeExportName(this.script.title);
    const exports: Record<string, () => void> = {
      txt: () => downloadFile(`${name}.txt`, exportText(this.script!)),
      fountain: () => downloadFile(`${name}.fountain`, exportFountain(this.script!), "text/plain"),
      fdx: () => downloadFile(`${name}.fdx`, exportFdx(this.script!), "application/xml"),
      json: () => downloadFile(`${name}.linebyline.json`, exportJson(this.script!), "application/json"),
      md: () => downloadFile(`${name}.md`, exportMarkdown(this.script!), "text/markdown"),
      print: () => window.print(),
    };
    exports[format]?.();
  }

  private currentLine(): ScriptEntry | undefined {
    return this.lines[this.position];
  }

  private resetPosition(): void {
    this.position = 0;
    this.revealedWords = 0;
    this.feedback = "";
  }

  private async persist(): Promise<void> {
    if (this.script && !this.joinLobby && !this.collaboration.isClient()) await store.save(this.script);
  }

  private savePosition(): void {
    if (!this.script || this.joinLobby || this.collaboration.isClient()) return;
    const current = this.currentLine();
    const entryIndex = current ? Math.max(0, this.allLines.findIndex((line) => line.id === current.id)) : 0;
    void playbackRepository.save({ scriptId: this.script.id, entryIndex, updatedAt: new Date().toISOString() });
  }
}

customElements.define("script-player", ScriptPlayer);
